"use client"

import styled from "@emotion/styled"
import { Button, IconButton, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { Link, Result } from "utils"

const EmptyResultsBox = styled.div`
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  height: 400px;
  justify-content: center;
  align-items: center;
`
const EmptyText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const StyledSearchResultsContainer = styled.div<{
  $width?: string
  $top?: string
  $height?: string
}>`
  border-radius: 0px 0px 16px 16px;
  background: #fff;
  width: 100%;
  overflow: hidden;
`

const StyledSearchResults = styled.div<{
  $width?: string
  $top?: string
  $height?: string
}>`
  height: auto;
  max-height: ${(p) => (p.$height ? p.$height : "550px")};
  overflow-y: auto;
  border-radius: 0px 0px 16px 16px;
  border-top: 1px solid #d1d5db;
  a {
    text-decoration: none;
  }

  ::-webkit-scrollbar {
    width: 20px;
  }

  ::-webkit-scrollbar-track {
    background: #fff;
  }

  ::-webkit-scrollbar-thumb {
    background: "blue";
    border: 8px solid #fff;
    border-radius: 200px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: "blue";
  }
`
const StyledSearchedItem = styled.div`
  display: flex;
  padding: 12px;
  justify-content: flex-end;
  gap: 16px;
  width: 100%;
  border-bottom: 1px solid #f5f6f8;
`

const StyledInfo = styled.div`
  display: flex;
  flex-direction: "row";
  justify-content: space-between;
  gap: 8px;
  text-align: left;
  ${(props) => props.theme.breakpoints.up("md")} {
    flex-grow: 1;
    gap: 16px;
  }
`

const StyledTitle = styled.div`
  display: flex;
  flex-direction: column;
`
const StyledImage = styled.div`
  border-radius: 16px;
  overflow: hidden;
`

function EmptyBox({
  onClick,
  term,
  locale,
}: {
  onClick: () => void
  term: string
  locale: string
}) {
  const t = useTranslations("Homepage")
  const tInner = useTranslations("InnerPages")
  return (
    <StyledSearchResultsContainer>
      <StyledSearchResults>
        <EmptyResultsBox>
          <Image src="/images/cactus.png" alt="" width={168} height={168} />
          <EmptyText>
            <Typography variant="h3" role="alert" tabIndex={0}>
              {t("noResultsFound")}
            </Typography>
            <Typography variant="body2">
              {t("weDidNotFindRes")}
              {` ${term}`}
            </Typography>
          </EmptyText>

          <Button
            variant="secondary"
            style={{ width: "300px" }}
            onClick={onClick}
          >
            {tInner("backToHomepage")}
          </Button>
        </EmptyResultsBox>
      </StyledSearchResults>
    </StyledSearchResultsContainer>
  )
}

type Props = {
  handleClose: () => void
  array: Result
  term: string
  locale: string
  closeModal: () => void
}
function Results({ handleClose, array, term, locale, closeModal }: Props) {
  if (!array.length) {
    return <EmptyBox onClick={handleClose} term={term} locale={locale} />
  }

  return (
    <StyledSearchResultsContainer>
      <StyledSearchResults>
        {array.map((item, index: number) => {
          return (
            <Link href={item.url} onClick={closeModal}>
              <StyledSearchedItem key={index.toString()}>
                <StyledImage></StyledImage>
                <StyledInfo>
                  <StyledTitle>
                    <Typography variant="body1" fontWeight={700}>
                      {item.title}
                    </Typography>
                  </StyledTitle>
                </StyledInfo>
                <IconButton
                  role="presentation"
                  aria-hidden="true"
                  tabIndex={-1}
                >
                  <Image
                    src="/icons/arrow-left.svg"
                    alt="close"
                    width={24}
                    height={24}
                    style={{
                      transform:
                        locale === "en" || locale === "ru"
                          ? "rotate(180deg)"
                          : "none",
                    }}
                  />
                </IconButton>
              </StyledSearchedItem>
            </Link>
          )
        })}
      </StyledSearchResults>
    </StyledSearchResultsContainer>
  )
}

export default Results
